'use client';

// module imports
import { useEffect, useState } from 'react';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  isDeleting?: boolean;
  requireTextInput?: boolean;
  itemName?: string;
}

export default function DeleteConfirmationModal({ isOpen, onClose, onConfirm, title, message, confirmText = 'DELETE', isDeleting = false, requireTextInput = true, itemName = 'item' }: DeleteConfirmationModalProps) {
  const [confirmationText, setConfirmationText] = useState('');

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset confirmation text when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const isTextValid = requireTextInput ? confirmationText.toLowerCase() === confirmText.toLowerCase() : true;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 overflow-hidden min-h-screen">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose}></div>

      {/* Modal content */}
      <div className="relative bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 max-w-xs sm:max-w-md lg:max-w-lg w-full shadow-2xl border border-gray-200 z-[10000] mx-auto">
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{title}</h3>
        </div>

        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 break-words whitespace-normal">{message}</p>

        {requireTextInput && (
          <div className="mb-4 sm:mb-6">
            <label htmlFor={`delete-confirmation-${itemName}`} className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 break-words">
              Type <span className="font-bold text-red-600">{confirmText}</span> to confirm:
            </label>
            <input id={`delete-confirmation-${itemName}`} type="text" value={confirmationText} onChange={(e) => setConfirmationText(e.target.value)} placeholder={`Type ${confirmText} here`} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-gray-300" autoComplete="off" />
          </div>
        )}

        {!requireTextInput && <div className="mb-4 sm:mb-6"></div>}

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
          <button onClick={handleClose} className="px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium cursor-pointer order-2 sm:order-1 whitespace-nowrap">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={isDeleting || !isTextValid} className="px-4 py-2 text-sm sm:text-base bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium cursor-pointer order-1 sm:order-2 whitespace-nowrap">
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
